import React, { useState, useEffect } from 'react';
import { Check, Plus, X } from 'lucide-react';
import { useOnboarding } from '../../hooks/useOnboarding';
import Button from '../../../../components/base/Button';

const PREDEFINED_CATEGORIES = [
  { id: 'food', name: 'Alimentación', icon: '🍽️', popular: true },
  { id: 'transport', name: 'Transporte', icon: '🚗', popular: true },
  { id: 'housing', name: 'Vivienda', icon: '🏠', popular: true },
  { id: 'entertainment', name: 'Entretenimiento', icon: '🎬', popular: true },
  { id: 'health', name: 'Salud', icon: '⚕️', popular: false },
  { id: 'education', name: 'Educación', icon: '📚', popular: false },
  { id: 'shopping', name: 'Compras', icon: '🛍️', popular: false },
  { id: 'technology', name: 'Tecnología', icon: '💻', popular: false },
  { id: 'travel', name: 'Viajes', icon: '✈️', popular: false },
  { id: 'personal-care', name: 'Cuidado Personal', icon: '💄', popular: false },
  { id: 'gifts', name: 'Regalos', icon: '🎁', popular: false },
  { id: 'other', name: 'Otros', icon: '📦', popular: false }
];

export const CategoriesStep: React.FC = () => {
  const { userPreferences, saveUserPreferences } = useOnboarding();
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [customCategory, setCustomCategory] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  // Cargar categorías seleccionadas desde las preferencias
  useEffect(() => {
    if (userPreferences?.categories) {
      setSelectedCategories(userPreferences.categories);
    }
  }, [userPreferences]);

  const handleToggleCategory = (categoryName: string) => {
    setSelectedCategories(prev => {
      const newCategories = prev.includes(categoryName)
        ? prev.filter(cat => cat !== categoryName)
        : [...prev, categoryName];
      
      // Guardar inmediatamente las categorías seleccionadas
      saveUserPreferences({ categories: newCategories });
      
      return newCategories;
    });
  };

  const handleAddCustomCategory = () => {
    if (customCategory.trim() && !selectedCategories.includes(customCategory.trim())) {
      const newCategories = [...selectedCategories, customCategory.trim()];
      setSelectedCategories(newCategories);
      saveUserPreferences({ categories: newCategories });
      setCustomCategory('');
      setShowCustomInput(false);
    }
  };

  const handleRemoveCustomCategory = (categoryName: string) => {
    // Solo permitir eliminar categorías que no están en las predefinidas
    const isPredefined = PREDEFINED_CATEGORIES.some(cat => cat.name === categoryName);
    if (!isPredefined) {
      handleToggleCategory(categoryName);
    }
  };

  const popularCategories = PREDEFINED_CATEGORIES.filter(cat => cat.popular);
  const otherCategories = PREDEFINED_CATEGORIES.filter(cat => !cat.popular);
  const customCategories = selectedCategories.filter(
    cat => !PREDEFINED_CATEGORIES.some(predefined => predefined.name === cat)
  );

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h4 className="text-lg font-semibold text-gray-900 mb-2">
          Selecciona tus categorías de gastos
        </h4>
        <p className="text-gray-600">
          Escoge las categorías que más uses. Podrás modificarlas más tarde.
        </p>
      </div>

      {/* Categorías populares */}
      <div>
        <h5 className="font-medium text-gray-900 mb-3 flex items-center">
          <span className="mr-2">🔥</span>
          Más populares
        </h5>
        <div className="grid grid-cols-2 gap-3">
          {popularCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => handleToggleCategory(category.name)}
              className={`flex items-center space-x-3 p-3 rounded-lg border transition-all ${
                selectedCategories.includes(category.name)
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="text-lg">{category.icon}</span>
              <span className="font-medium">{category.name}</span>
              {selectedCategories.includes(category.name) && (
                <Check className="w-4 h-4 ml-auto text-blue-600" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Otras categorías */}
      <div>
        <h5 className="font-medium text-gray-900 mb-3">
          Otras categorías
        </h5>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
          {otherCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => handleToggleCategory(category.name)}
              className={`flex items-center space-x-2 p-2 rounded-lg border text-sm transition-all ${
                selectedCategories.includes(category.name)
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
              }`}
            >
              <span>{category.icon}</span>
              <span>{category.name}</span>
              {selectedCategories.includes(category.name) && (
                <Check className="w-3 h-3 ml-auto text-blue-600" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Categorías personalizadas */}
      {customCategories.length > 0 && (
        <div>
          <h5 className="font-medium text-gray-900 mb-3">
            Tus categorías personalizadas
          </h5>
          <div className="flex flex-wrap gap-2">
            {customCategories.map((category) => (
              <div
                key={category}
                className="flex items-center space-x-2 bg-green-50 border border-green-200 text-green-700 px-3 py-1 rounded-full text-sm"
              >
                <span>📝</span>
                <span>{category}</span>
                <button
                  onClick={() => handleRemoveCustomCategory(category)}
                  className="text-green-600 hover:text-green-800"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Agregar categoría personalizada */}
      <div>
        {showCustomInput ? (
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={customCategory}
              onChange={(e) => setCustomCategory(e.target.value)}
              placeholder="Nombre de la categoría..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              onKeyPress={(e) => e.key === 'Enter' && handleAddCustomCategory()}
              autoFocus
            />
            <Button
              onClick={handleAddCustomCategory}
              disabled={!customCategory.trim()}
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Agregar
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowCustomInput(false);
                setCustomCategory('');
              }}
            >
              Cancelar
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            onClick={() => setShowCustomInput(true)}
            className="flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Agregar categoría personalizada</span>
          </Button>
        )}
      </div>

      {/* Resumen */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">
            Categorías seleccionadas:
          </span>
          <span className="text-sm font-bold text-blue-600">
            {selectedCategories.length}
          </span>
        </div>
        {selectedCategories.length > 0 && (
          <div className="mt-2 text-xs text-gray-600">
            {selectedCategories.join(', ')}
          </div>
        )}
      </div>
    </div>
  );
};